/**
 * Function naming convention: api{FunctionName}
 * File naming convention: {FunctionName}.ts
 */

import { API_REQUEST_PARAMS } from "@tradingApp/api/constants";
import { apiAbortControllers } from "@tradingApp/api/helpers/abortControllers";
import type { ApiType } from "@tradingApp/api/types";
import { isFunction } from "@tradingApp/utils/isFunction";
import WebApp from "@twa-dev/sdk";
import isObject from "lodash/isObject";
import { version } from "../../../../package.json";

export interface APIResponseBody<Body> {
  ok: boolean;
  err: string;
  statusCode: number;
  statusText: string;
  body: Body;
}

/**
 * @TODO Refactoring: Pass params as an object instead of multiple arguments
 */
export async function apiRequest<T>(
  path: string,
  options?: RequestInit,
  apiType: ApiType = "DPS",
  accessToken?: string | null,
  beforeSend?: ((options: RequestInit) => void) | null,
  timeout = 60000,
): Promise<APIResponseBody<T>> {
  try {
    const { baseUrl, options: defaultOptions } =
      API_REQUEST_PARAMS[apiType] || {};

    const headers = new Headers(defaultOptions.headers);

    if (version && apiType !== "HYPERUNIT" && apiType !== "HYPER_PARSER") {
      headers.set("App-Version", version);
    }

    if (
      apiType === "DPS" ||
      apiType === "DPS_SERVICE" ||
      apiType === "HYPER_PARSER"
    ) {
      headers.set("X-TG-DATA", WebApp.initData);
    }

    if (accessToken) {
      headers.set("Authorization", `Bearer ${accessToken}`);
    }

    const requestUrl = addPathToUrl(path, baseUrl);
    const { signal } = apiAbortControllers.create(requestUrl);

    const id = setTimeout(
      () => apiAbortControllers.cancel(requestUrl),
      timeout,
    );

    const requestOptions = {
      ...defaultOptions,
      ...(isObject(options) ? options : {}),
      signal,
      headers,
    };

    if (isFunction(beforeSend)) {
      beforeSend(requestOptions);
    }

    const res = await fetch(requestUrl, {
      ...defaultOptions,
      ...(isObject(options) ? { ...options, signal } : {}),
      headers,
    });

    clearTimeout(id);

    const method = options?.method || defaultOptions.method;
    let body = {} as T;

    try {
      body = await res.json();
    } catch (_) {
      body = {
        message: res.statusText,
      } as T;
    }

    if ((res.status === 200 && method === "DELETE") || res.status === 204) {
      return {
        ok: true,
        err: "",
        statusCode: res.status,
        statusText: res.statusText,
        body: true as T,
      };
    }

    if (!res.ok) {
      return {
        ok: false,
        err: (body as { message: string })?.message || "",
        statusCode: res.status,
        statusText: res.statusText,
        body: null as T,
      };
    }

    return {
      ok: res.ok,
      err: "",
      statusCode: res.status,
      statusText: res.statusText,
      body,
    };
  } catch (err) {
    console.warn(err);
    let errorMessage = "";

    if (err instanceof Error) {
      if (err.name === "TimeoutError") {
        errorMessage = "Request Timeout";
      } else if (err.name === "AbortError") {
        errorMessage = "Request Aborted";
      } else {
        errorMessage = err.message;
      }
    }

    return {
      ok: false,
      err: errorMessage,
      statusCode: 520,
      statusText: "Unexpected error occurred",
      body: null as T,
    };
  }
}

function addPathToUrl(path: string, baseUrl: string): string {
  return `${baseUrl.replace(/\/$/g, "")}/${path.replace(/^\//g, "")}`;
}
